/* eslint-disable no-console */
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {Flex, Form, Select, Tooltip} from 'antd';
import React, {isValidElement, useEffect, useState} from 'react';

import {ReactComponent as PeerAveragePlus} from '../../../../assets/icons/PeerAveragePlus.svg';
import PilComp from '../../../../components/DisplayComponent/PilComp';
import {
  ESGFrameworkDetail,
  PeerAveragePopup,
  PeerSourceInfoPopup
} from '../../../../components/DisplayModal/ESGFramework/ESGFrameworkDetail';
import Loader from '../../../../components/Loader/Loader';
import {usePeerUniverse} from '../../../../hooks/usePeerUniverse';
import {
  getEntityPeerBenchmark,
  type EntityPeerBenchmark
} from '../../../../services/modules/ClimateDB/peerBenchmarkAPI';
import useCompanyStore from '../../../../store/companyStore/companyDetailsStore';
import {toIdArray} from '../../../../utils/companyCategorySubCategory';
import {OPKey} from '../../../configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';

import type {PeerCompanyNameOption} from './AddPeerCompaniesModal';
import {
  // Static fallback disabled: the page shows the live API response only.
  // getPeerBenchmarkDataArray,
  resolvePeerBenchmarkMetricView
} from './constants/peerBenchmarkingDataResolver';

interface PeerBenchMarkingContentProps {
  selectedYear: string;
  onPeerGroupName?: (name: string | null) => void;
  onPeerCompanies?: (companies: PeerCompanyNameOption[]) => void;
  onShownPeerNames?: (names: string[]) => void;
  requestedPeerIds?: number[] | null;
}

// Static fallback disabled: only used to format the static peer values.
// function formatEnvironmentalCompanyValue(value: number | string): string {
//   const text = String(value).trim();
//   if (/^-?\d+(\.\d+)?$/.test(text)) {
//     return `${text} tCO2e`;
//   }
//
//   return text;
// }

const PeerBenchMarkingContent: React.FC<PeerBenchMarkingContentProps> = ({
  selectedYear,
  onPeerGroupName,
  onPeerCompanies,
  onShownPeerNames,
  requestedPeerIds
}) => {
  const [showEsgDetailModal, setShowEsgDetailModal] = useState(false);
  const [showPeerAveragePopup, setShowPeerAveragePopup] = useState(false);
  const [showSourceInfoModal, setShowSourceInfoModal] = useState(false);
  const [peerAverageDetail, setPeerAverageDetail] = useState('');
  const [selectedMericsIndex, setSelectedMericsIndex] = useState(0);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [selectedCompanyDetail, setSelectedCompanyDetail] = useState('');
  const [selectedMetricLabel, setSelectedMetricLabel] = useState<
    Record<number, string>
  >({});
  const [selectedMetricValue, setSelectedMetricValue] = useState<
    Record<string, string>
  >({});
  const [apiResult, setApiResult] = useState<EntityPeerBenchmark | null>(null);
  const [isLoadingPeers, setIsLoadingPeers] = useState(true);
  const companyId = useCompanyStore((state) => state.companyId);
  const {
    universe,
    selectedPeerIds,
    setSelectedPeerIds,
    isLoading: isUniverseLoading
  } = usePeerUniverse(companyId, selectedYear);
  // Static fallback disabled: only used to list user-added peers over it.
  // const userPeerCompanies = useCompanyStore(
  //   (state) => state.userPeerCompanies
  // );

  const companyDetails = useCompanyStore.getState().companyDetails;
  const subCategory = companyDetails[OPKey.SUB_CATEGORY];
  const subCategoryKey = toIdArray(subCategory).join(',') || '0';
  useEffect(() => {
    onPeerCompanies?.(
      (universe?.peers ?? []).map((peer) => ({
        value: peer.legal_name,
        label: peer.legal_name,
        entityId: peer.entity_id
      }))
    );
  }, [onPeerCompanies, universe]);

  useEffect(() => {
    if (!apiResult) {
      return;
    }

    // Every peer company listed under any metric, so the add-peers modal can
    // preselect exactly what the page is showing.
    const shownNames = new Set<string>();
    apiResult.pillars.forEach((pillar) => {
      Object.values(pillar.metricData ?? {}).forEach((view) => {
        view.companyData.forEach((company) => {
          shownNames.add(company.label);
        });
      });
    });
    onShownPeerNames?.(Array.from(shownNames));
  }, [apiResult, onShownPeerNames]);

  // const usingLiveData = apiResult !== null;
  // const dataArray = apiResult
  //   ? apiResult.pillars
  //   : getPeerBenchmarkDataArray(subCategory, selectedYear);
  const dataArray = apiResult?.pillars ?? [];

  useEffect(() => {
    if (!requestedPeerIds) {
      return;
    }

    setSelectedPeerIds(requestedPeerIds);
  }, [requestedPeerIds]);

  useEffect(() => {
    // Wait for the universe so the first benchmark call already carries the
    // default selection, instead of fetching the whole group and refetching.
    if (isUniverseLoading) {
      return;
    }

    let active = true;
    setIsLoadingPeers(true);
    onPeerGroupName?.(null);

    void getEntityPeerBenchmark(companyId, selectedYear, selectedPeerIds).then(
      (result) => {
        if (!active) {
          return;
        }

        setApiResult(result);
        onPeerGroupName?.(result?.peerGroupName ?? null);
        setIsLoadingPeers(false);
      }
    );

    return () => {
      active = false;
    };
  }, [
    companyId,
    onPeerGroupName,
    selectedYear,
    selectedPeerIds,
    isUniverseLoading
  ]);

  if (isLoadingPeers) {
    return (
      <div style={{minHeight: 480}}>
        <Loader isHome={true} />
      </div>
    );
  }

  if (!dataArray.length) {
    return (
      <div
        style={{
          padding: '24px',
          color: '#404252',
          font: 'normal normal 600 14px Montserrat'
        }}
      >
        No peer benchmark data for this year.
      </div>
    );
  }

  const metricSelectionKey = (index: number) =>
    `${subCategoryKey}-${selectedYear}-${index}`;

  return (
    <>
      {dataArray?.map((item, index) => {
        const selectionKey = metricSelectionKey(index);
        const metricOptions = item.metrics ?? [];
        const defaultMetricValue = metricOptions[0]?.value ?? '';
        const storedMetricValue = selectedMetricValue[selectionKey];
        const sortedMetricOptions = [...metricOptions].sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        const metricValue = metricOptions.some(
          (metric) => metric.value === storedMetricValue
        )
          ? storedMetricValue
          : defaultMetricValue;
        const view = resolvePeerBenchmarkMetricView(item, metricValue);
        // Static fallback disabled: user-added peers were shown as "NA" rows
        // over the static data.
        // const view =
        //   !usingLiveData && userPeerCompanies.length
        //     ? {
        //         average: 'NA',
        //         averageDetail:
        //           'AI analysis was completed for the submitted peer companies.',
        //         companyData: userPeerCompanies.map((company) => ({
        //           name: company.companyName,
        //           label: company.companyName,
        //           value: 'NA',
        //           detail: `Company website: ${company.companyWebsite}`
        //         }))
        //       }
        //     : defaultView;

        return (
          <Flex
            style={{
              width: '100%',
              backgroundColor: '#ffffff',
              border: '1px solid #F3F4F8',
              borderRadius: '12px',
              padding: '20px'
            }}
            gap={45}
            key={`${item.pillar}_${index}`}
          >
            <Flex vertical gap={25} style={{width: '40%'}}>
              <PilComp
                value={item?.pillar}
                style={{
                  height: '35px',
                  alignContent: 'center',
                  color: '#000000',
                  font: 'normal normal 700 16px Montserrat',
                  padding: '8px 16px',
                  backgroundColor: item?.color
                }}
              />
              <Form.Item label="ESG Metric" labelCol={{span: 24}}>
                <Select
                  key={selectionKey}
                  style={{
                    height: '48px',
                    font: 'normal normal 700 16px Montserrat'
                  }}
                  value={metricValue === '' ? undefined : metricValue}
                  options={sortedMetricOptions}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Choose ESG Metric"
                  onChange={(
                    value: string,
                    option:
                      | {label?: string; children?: string}
                      | Array<{label?: string; children?: string}>
                  ) => {
                    const selectedOption = Array.isArray(option)
                      ? option[0]
                      : option;
                    const label =
                      selectedOption?.label ?? selectedOption?.children ?? '';
                    setSelectedMetricValue((prev) => ({
                      ...prev,
                      [selectionKey]: value
                    }));
                    setSelectedMetricLabel((prev) => ({
                      ...prev,
                      [index]: label
                    }));
                  }}
                />
              </Form.Item>
            </Flex>
            <Flex vertical style={{width: '60%'}} gap={6}>
              <Flex
                style={{
                  border: '1px solid #B3B5BD',
                  borderRadius: '4px',
                  height: '55px',
                  backgroundColor: item?.color,
                  padding: '12px'
                }}
                align="center"
                justify="space-between"
              >
                <div
                  style={{
                    color: '#000000',
                    font: 'normal normal 700 16px Montserrat',
                    alignContent: 'center'
                  }}
                >
                  Peer Average
                </div>
                <div
                  style={{
                    borderRadius: '100px',
                    backgroundColor: item?.pillColor,
                    width: isValidElement(view?.average)
                      ? '32px'
                      : 'fit-content',
                    height: isValidElement(view?.average)
                      ? '32px'
                      : 'fit-content',
                    padding: isValidElement(view?.average) ? '8px' : '8px 16px',
                    font: 'normal normal 700 16px Montserrat'
                  }}
                  onClick={() => {
                    setPeerAverageDetail(view?.averageDetail);
                    setShowPeerAveragePopup(true);
                  }}
                >
                  {view?.average}
                </div>
              </Flex>
              <Flex
                style={{
                  border: '1px solid #B3B5BD',
                  borderRadius: '4px',
                  height: 'auto',
                  maxHeight: '138px',
                  padding: '12px 10px',
                  overflowY: 'scroll'
                }}
                className="light-scroll"
                gap={8}
                vertical
              >
                <Flex align="center" gap={8}>
                  <div
                    style={{
                      color: '#000000',
                      font: 'normal normal 700 15px Montserrat'
                    }}
                  >
                    Peer Companies
                  </div>
                  <Tooltip title="Source of Information" arrow>
                    <span
                      role="button"
                      tabIndex={0}
                      style={{display: 'inline-flex', cursor: 'pointer'}}
                      onClick={() => {
                        setShowSourceInfoModal(true);
                      }}
                    >
                      <ExclamationCircleOutlined
                        style={{
                          color: '#99b1c7',
                          fontSize: 16
                        }}
                      />
                    </span>
                  </Tooltip>
                </Flex>
                {view?.companyData?.map((innerItem, innerIndex) => {
                  return (
                    <Flex
                      vertical={false}
                      align="center"
                      justify="space-between"
                      key={`${innerItem.label}_${innerIndex}`}
                    >
                      <div
                        style={{
                          color: '#404252',
                          font: 'normal normal 700 14px Montserrat'
                        }}
                      >
                        {innerItem?.label}
                      </div>
                      <div>
                        {String(innerItem?.value ?? '').trim() !== '' ? (
                          <PilComp
                            value={String(innerItem.value ?? '')}
                            style={{
                              color: '#000000',
                              font: 'normal normal 700 12px Montserrat',
                              padding: '4px 8px',
                              backgroundColor: item?.color
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              height: '24px',
                              aspectRatio: '1',
                              borderRadius: '100px',
                              border: '1px solid #719A5D',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                            onClick={() => {
                              setSelectedMericsIndex(index);
                              setSelectedCompanyName(innerItem?.label);
                              setSelectedCompanyDetail(innerItem?.detail);
                              setShowEsgDetailModal(true);
                            }}
                          >
                            <PeerAveragePlus />
                          </div>
                        )}
                      </div>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
            <>
              <ESGFrameworkDetail
                companyName={selectedCompanyName}
                headerTitle={selectedMetricLabel[selectedMericsIndex]}
                contentDetail={selectedCompanyDetail}
                open={showEsgDetailModal}
                onClose={() => {
                  setShowEsgDetailModal(false);
                }}
              />
              <PeerAveragePopup
                contentDetail={peerAverageDetail}
                open={showPeerAveragePopup}
                onClose={() => {
                  setShowPeerAveragePopup(false);
                }}
              />
            </>
          </Flex>
        );
      })}
      <PeerSourceInfoPopup
        open={showSourceInfoModal}
        onClose={() => {
          setShowSourceInfoModal(false);
        }}
        selectedYear={selectedYear}
      />
    </>
  );
};

export default PeerBenchMarkingContent;
